import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

/** Show a measure number above the track at every Nth measure (1-based). */
const LABEL_INTERVAL = 5;

/**
 * Overview bar: a horizontal strip of rectangles (one per measure) with a
 * draggable thumb that seeks the playback cursor to the start of any measure.
 * Fills the remaining horizontal space in the footer and stays large enough to
 * drag with a finger on a tablet.
 *
 * The thumb also tracks the live cursor during playback (progress indicator).
 * `currentMeasure` is written by the host from playback and moves the thumb
 * WITHOUT dispatching an event; `measure-seek` fires only on user interaction.
 *
 * @fires measure-seek - detail: { measure: number } (0-based, clamped)
 */
@customElement('overview-bar')
export class OverviewBar extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: flex;
                flex-direction: column;
                justify-content: center;
                flex: 1 1 auto;
                min-width: 0;
                height: 44px;
                padding: 0 0.5rem;
                /* Prevent the page from scrolling while dragging on a tablet. */
                touch-action: none;
            }

            :host([disabled]) {
                opacity: 0.5;
                pointer-events: none;
            }

            /* Measure-number ticks above the track (every 5th measure). */
            .labels {
                position: relative;
                height: 11px;
                margin-bottom: 2px;
            }

            .label {
                position: absolute;
                bottom: 0;
                transform: translateX(-50%);
                font-size: 9px;
                line-height: 1;
                color: var(--text-secondary, #94a3b8);
                user-select: none;
                -webkit-user-select: none;
                pointer-events: none;
                white-space: nowrap;
            }

            .track {
                position: relative;
                flex: 1 1 auto;
                min-width: 0;
                height: 14px;
                border-radius: 7px;
                overflow: hidden;
                background: var(--surface-light, #334155);
                border: 1px solid var(--border, #475569);
                cursor: pointer;
            }

            .track.empty {
                cursor: default;
                opacity: 0.4;
            }

            .track:focus-visible {
                outline: 2px solid var(--primary-hover, #3b82f6);
                outline-offset: 2px;
            }

            /* Discrete rectangles: equal-share flex cells with hairline gaps. */
            .rects-fill {
                position: absolute;
                inset: 0;
                display: flex;
                gap: 1px;
            }

            .rect {
                flex: 1 1 0;
                min-width: 0;
                background: var(--surface, #1e293b);
            }

            /* Focus-area highlight: measures the player fumbles/hesitates on
               most (from practice history). Amber tint, readable in light and
               dark, and it sits UNDER the translucent .progress band so a
               played problem cell still reads as a problem. */
            .rect.problem {
                background: color-mix(in srgb, var(--warning-color, #f59e0b) 70%, var(--surface, #1e293b));
            }

            /* Played-measures shading; width updated in O(1) per playback tick. */
            .progress {
                position: absolute;
                inset: 0 auto 0 0;
                background: color-mix(in srgb, var(--primary-color, #2563eb) 45%, transparent);
                pointer-events: none;
            }

            /* Draggable thumb — the track owns all pointer events, so the thumb
               is purely visual (pointer-events: none). */
            .thumb {
                position: absolute;
                top: 50%;
                transform: translate(-50%, -50%);
                width: 4px;
                height: 24px;
                border-radius: 3px;
                background: var(--text-primary, #f1f5f9);
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
                pointer-events: none;
            }
        `,
    ];

    /** Total number of measures in the score. 0 => empty / nothing to show. */
    @property({ type: Number })
    measureCount = 0;

    /**
     * Zero-based index of the measure the cursor is currently in. Written by
     * the host from playback; drives the thumb/progress only and never
     * re-dispatches an event.
     */
    @property({ type: Number })
    currentMeasure = 0;

    @property({ type: Boolean, reflect: true })
    disabled = false;

    /**
     * Zero-based measure indexes to tint as "focus areas" (practice trouble
     * spots). Empty => a plain bar. Shown only in practice/test modes; the host
     * assigns/clears this on mode entry/exit.
     */
    @property({ type: Array })
    problemMeasures: number[] = [];

    /** O(1)-lookup mirror of `problemMeasures`, rebuilt on change. */
    private _problemSet = new Set<number>();

    private _dragging = false;
    private _rafPending = false;
    private _pendingMeasure: number | null = null;
    private _lastSentMeasure = -1;

    willUpdate(changed: Map<string, unknown>): void {
        if (changed.has('problemMeasures')) {
            this._problemSet = new Set(this.problemMeasures);
        }
    }

    /** Map a pointer's clientX to a measure index within the track. */
    private _measureFromClientX(clientX: number): number {
        const track = this.renderRoot.querySelector('.track') as HTMLElement | null;
        if (!track || this.measureCount <= 0) return 0;
        const rect = track.getBoundingClientRect();
        if (rect.width <= 0) return 0;
        const frac = (clientX - rect.left) / rect.width;
        const idx = Math.floor(frac * this.measureCount);
        return Math.max(0, Math.min(this.measureCount - 1, idx));
    }

    private _onPointerDown(e: PointerEvent): void {
        if (this.disabled || this.measureCount <= 0) return;
        // Left button for mouse; always allow touch/pen.
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        e.preventDefault();
        const track = e.currentTarget as HTMLElement;
        // Best-effort: capture failures (e.g. no active pointer) must not abort
        // the seek — the interaction still works via the track's own listeners.
        try {
            track.setPointerCapture(e.pointerId);
        } catch {
            /* ignore — pointer capture is an enhancement, not a requirement */
        }
        this._dragging = true;
        this._lastSentMeasure = -1; // force the first emit
        // Immediate jump on tap-down (also handles a plain click/tap).
        this._queueMeasure(this._measureFromClientX(e.clientX));

        track.addEventListener('pointermove', this._onPointerMove);
        track.addEventListener('pointerup', this._onPointerUp);
        track.addEventListener('pointercancel', this._onPointerUp);
    }

    private _onPointerMove = (e: PointerEvent): void => {
        if (!this._dragging) return;
        this._queueMeasure(this._measureFromClientX(e.clientX));
    };

    private _onPointerUp = (e: PointerEvent): void => {
        if (!this._dragging) return;
        this._dragging = false;
        const track = e.currentTarget as HTMLElement;
        try {
            track.releasePointerCapture(e.pointerId);
        } catch {
            /* pointer may already be released */
        }
        track.removeEventListener('pointermove', this._onPointerMove);
        track.removeEventListener('pointerup', this._onPointerUp);
        track.removeEventListener('pointercancel', this._onPointerUp);
        // Flush any pending measure so the final position is always sent.
        if (this._pendingMeasure !== null) this._flushMeasure();
    };

    private _queueMeasure(measure: number): void {
        this._pendingMeasure = measure;
        // Optimistic local move: keep the thumb glued to the finger. The host
        // re-asserts currentMeasure from the seek's positionChanged, converging
        // on the same value, so there is no visible jump.
        if (measure !== this.currentMeasure) this.currentMeasure = measure;
        if (this._rafPending) return;
        this._rafPending = true;
        requestAnimationFrame(() => {
            this._rafPending = false;
            this._flushMeasure();
        });
    }

    private _flushMeasure(): void {
        if (this._pendingMeasure === null) return;
        const measure = this._pendingMeasure;
        this._pendingMeasure = null;
        if (measure === this._lastSentMeasure) return; // de-dupe identical seeks
        this._lastSentMeasure = measure;
        this.dispatchEvent(
            new CustomEvent<{ measure: number }>('measure-seek', {
                bubbles: true,
                composed: true,
                detail: { measure },
            })
        );
    }

    private _onKeyDown(e: KeyboardEvent): void {
        if (this.disabled || this.measureCount <= 0) return;
        let target = this.currentMeasure;
        switch (e.key) {
            case 'ArrowLeft':
            case 'ArrowDown':
                target -= 1;
                break;
            case 'ArrowRight':
            case 'ArrowUp':
                target += 1;
                break;
            case 'Home':
                target = 0;
                break;
            case 'End':
                target = this.measureCount - 1;
                break;
            default:
                return;
        }
        e.preventDefault();
        target = Math.max(0, Math.min(this.measureCount - 1, target));
        this.currentMeasure = target;
        this._lastSentMeasure = target;
        this.dispatchEvent(
            new CustomEvent<{ measure: number }>('measure-seek', {
                bubbles: true,
                composed: true,
                detail: { measure: target },
            })
        );
    }

    render() {
        const n = this.measureCount;
        if (n <= 0) {
            return html`<div class="track empty"></div>`;
        }

        const cur = Math.max(0, Math.min(n - 1, this.currentMeasure));
        // Thumb/progress reach the center of the current measure.
        const thumbPct = ((cur + 0.5) / n) * 100;

        // Measure-number labels at every 5th measure (1-based: 5, 10, 15, ...),
        // each centered over its rectangle.
        const labels = [];
        for (let m = LABEL_INTERVAL; m <= n; m += LABEL_INTERVAL) {
            const leftPct = ((m - 0.5) / n) * 100;
            labels.push(
                html`<span class="label" style="left:${leftPct}%">${m}</span>`
            );
        }

        return html`
            <div class="labels">${labels}</div>
            <div
                class="track"
                role="slider"
                aria-label="Measure position"
                aria-valuemin="1"
                aria-valuemax=${n}
                aria-valuenow=${cur + 1}
                tabindex=${this.disabled ? -1 : 0}
                @pointerdown=${this._onPointerDown}
                @keydown=${this._onKeyDown}
            >
                <div class="rects-fill">
                    ${Array.from({ length: n }, (_, i) =>
                        html`<div class="rect ${this._problemSet.has(i) ? 'problem' : ''}"></div>`
                    )}
                </div>
                <div class="progress" style="width:${thumbPct}%"></div>
                <div class="thumb" style="left:${thumbPct}%" aria-hidden="true"></div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'overview-bar': OverviewBar;
    }
}
